import React, { useState, useEffect } from 'react';
import { Users, Crown, FileSignature, Eye, X, Check, Search } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BureauManager = ({ 
  selectedAG, 
  token, 
  participants,
  onBureauUpdated,
  onRefreshAG,
  isReadOnly = false 
}) => {
  const [bureau, setBureau] = useState({
    president: null,
    scrutateurs: [],
    secretaire: null
  });
  const [showSelector, setShowSelector] = useState(null); // 'president', 'scrutateur', 'secretaire'
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  // Load existing bureau
  useEffect(() => {
    loadBureau();
  }, [selectedAG?.id]);

  const loadBureau = async () => {
    if (!selectedAG?.id) return;
    try {
      const response = await axios.get(`${API}/assemblees/${selectedAG.id}/bureau`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setBureau({
          president: response.data.president || null,
          scrutateurs: response.data.scrutateurs || [],
          secretaire: response.data.secretaire || null
        });
      }
    } catch (err) {
      console.log('No bureau set yet');
    }
  };

  const saveBureau = async (newBureau) => {
    setSaving(true);
    try {
      await axios.post(`${API}/assemblees/${selectedAG.id}/bureau`, newBureau, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBureau(newBureau);
      if (onBureauUpdated) onBureauUpdated(newBureau);
      // Refresh AG data in parent to update AGCloseButton
      if (onRefreshAG) onRefreshAG();
    } catch (err) {
      alert('Erreur lors de la sauvegarde du bureau');
    } finally {
      setSaving(false);
    }
  };

  const selectMember = (participant, role) => {
    const memberData = {
      user_id: participant.user_id,
      nom: participant.nom,
      prenom: participant.prenom
    };

    let newBureau = { ...bureau };
    
    if (role === 'president') {
      newBureau.president = memberData;
    } else if (role === 'scrutateur') {
      // Check if already in scrutateurs
      const exists = newBureau.scrutateurs.some(s => s.user_id === participant.user_id);
      if (!exists) {
        newBureau.scrutateurs = [...newBureau.scrutateurs, memberData];
      }
    } else if (role === 'secretaire') {
      newBureau.secretaire = memberData;
    }

    saveBureau(newBureau);
    setShowSelector(null);
    setSearchTerm('');
  };

  const removeMember = (role, userId = null) => {
    let newBureau = { ...bureau };
    
    if (role === 'president') {
      newBureau.president = null;
    } else if (role === 'scrutateur' && userId) {
      newBureau.scrutateurs = newBureau.scrutateurs.filter(s => s.user_id !== userId);
    } else if (role === 'secretaire') {
      newBureau.secretaire = null;
    }

    saveBureau(newBureau);
  };

  // Filter eligible participants (only those present physically or via video/audio - NO correspondance or delegation)
  const eligibleParticipants = participants.filter(p => 
    p.mode === 'presentiel' || p.mode === 'video' || p.mode === 'audio'
  );

  const filteredParticipants = eligibleParticipants.filter(p => {
    const fullName = `${p.prenom || ''} ${p.nom || ''}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  const MemberCard = ({ title, icon: Icon, member, role, color }) => (
    <div className={`bg-white border-2 ${color} rounded-xl p-4`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <Icon size={18} className={color.replace('border-', 'text-')} />
          <span className="font-semibold text-gray-800">{title}</span>
        </div>
        {!isReadOnly && member && (
          <button
            onClick={() => removeMember(role)}
            className="text-gray-400 hover:text-red-500"
          >
            <X size={16} />
          </button>
        )}
      </div>
      
      {member ? (
        <div className="text-sm">
          <span className="font-medium">{member.prenom} {member.nom}</span>
          <span className="text-green-600 ml-2">✓ Désigné</span>
        </div>
      ) : (
        <div>
          {!isReadOnly ? (
            <button
              onClick={() => setShowSelector(role)}
              className="text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium"
            >
              + Désigner un coproprietaire
            </button>
          ) : (
            <span className="text-sm text-gray-400">Non désigné</span>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-gray-50 rounded-xl p-4 space-y-4">
      <h3 className="font-semibold text-gray-800 flex items-center space-x-2">
        <Users size={20} className="text-[#00D3A8]" />
        <span>Bureau de l'AG</span>
        {saving && <span className="text-xs text-gray-500 ml-2">(Sauvegarde...)</span>}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Président */}
        <MemberCard
          title="Président de séance"
          icon={Crown}
          member={bureau.president}
          role="president"
          color="border-amber-400"
        />
        
        {/* Scrutateur(s) */}
        <div className="bg-white border-2 border-blue-400 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <Eye size={18} className="text-blue-500" />
              <span className="font-semibold text-gray-800">Scrutateur(s)</span>
            </div>
          </div>
          
          {bureau.scrutateurs.length > 0 ? (
            <div className="space-y-1">
              {bureau.scrutateurs.map((s, idx) => (
                <div key={s.user_id} className="flex items-center justify-between text-sm">
                  <span className="font-medium">{s.prenom} {s.nom}</span>
                  {!isReadOnly && (
                    <button
                      onClick={() => removeMember('scrutateur', s.user_id)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              {!isReadOnly && (
                <button
                  onClick={() => setShowSelector('scrutateur')}
                  className="text-xs text-[#00D3A8] hover:text-[#00C7A1]"
                >
                  + Ajouter un scrutateur
                </button>
              )}
            </div>
          ) : (
            <div>
              {!isReadOnly ? (
                <button
                  onClick={() => setShowSelector('scrutateur')}
                  className="text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium"
                >
                  + Désigner un scrutateur
                </button>
              ) : (
                <span className="text-sm text-gray-400">Non désigné</span>
              )}
            </div>
          )}
        </div>
        
        {/* Secrétaire */}
        <MemberCard
          title="Secrétaire de séance"
          icon={FileSignature}
          member={bureau.secretaire}
          role="secretaire"
          color="border-purple-400"
        />
      </div>
      
      {/* Info légale */}
      <div className="text-xs text-gray-500 bg-yellow-50 p-2 rounded-lg">
        ⚠️ Les membres du bureau doivent être des coproprietaires présents physiquement ou en visioconférence. 
        La délégation n'est pas autorisée pour ces rôles.
      </div>
      
      {/* Selector Modal */}
      {showSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">
                  Sélectionner {showSelector === 'president' ? 'le Président' : 
                               showSelector === 'scrutateur' ? 'un Scrutateur' : 'le Secrétaire'}
                </h4>
                <button onClick={() => { setShowSelector(null); setSearchTerm(''); }}>
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              
              {/* Search */}
              <div className="mt-3 relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un coproprietaire..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
                />
              </div>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[50vh]">
              {filteredParticipants.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  Aucun coproprietaire éligible trouvé
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredParticipants.map((p) => {
                    // Check if already selected for another role
                    const isPresident = bureau.president?.user_id === p.user_id;
                    const isScrutateur = bureau.scrutateurs.some(s => s.user_id === p.user_id);
                    const isSecretaire = bureau.secretaire?.user_id === p.user_id;
                    const alreadySelected = isPresident || isScrutateur || isSecretaire;
                    
                    return (
                      <button
                        key={p.user_id}
                        onClick={() => !alreadySelected && selectMember(p, showSelector)}
                        disabled={alreadySelected}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          alreadySelected 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'hover:bg-[#00D3A8]/10 hover:border-[#00D3A8]'
                        }`}
                      >
                        <div className="font-medium">{p.prenom} {p.nom}</div>
                        <div className="text-xs text-gray-500 flex items-center space-x-2">
                          <span>{p.tantiemes} tantièmes</span>
                          <span>•</span>
                          <span className={`${
                            p.mode === 'presentiel' ? 'text-green-600' : 'text-blue-600'
                          }`}>
                            {p.mode === 'presentiel' ? '🏢 Présentiel' : '📹 Visioconférence'}
                          </span>
                          {alreadySelected && (
                            <>
                              <span>•</span>
                              <span className="text-amber-600">
                                {isPresident ? 'Président' : isScrutateur ? 'Scrutateur' : 'Secrétaire'}
                              </span>
                            </>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BureauManager;
